import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import BalanceRoundedIcon from '@mui/icons-material/BalanceRounded';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Dumbbell as WorkoutIcon, TrendingUp as GrowthPlannerIcon } from 'lucide-react';

import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import useToolPreferences from '../apps-explorer/useToolPreferences';
import useSurveyCoupons from '../apps-explorer/useSurveyCoupons';
import { APPS } from '../utils/cloudStorage';
import { usePremiumPrice } from '../utils/premiumPricing';
import corebasePng from '../img/devssoft-corebase.png';
import profitlensPng from '../img/devssoft-profitlens.png';
import AppsNav from '../apps-explorer/AppsNav';
import '../shell/appShell.css';
import '../apps-explorer/appsExplorer.css';

const ICON_COLOR = '#183B66';
const ICON_SIZE = 22;

const APP_ICONS = {
  personal_finance_v1: () => <AccountBalanceWalletIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  devssoft_forms_v1: () => <DescriptionRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  projectbase_pm_v1: () => <EventAvailableRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  fastfood_pos_v1: () => <FastfoodIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  devssoft_chat_v1: () => <SmartToyRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  retail_pos_v1: () => <LocalMallRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  corebase_crm_v1: () => (
    <img src={corebasePng} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
  ),
  loan_calculator: () => <CalculateRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  cv_builder: () => <DescriptionRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  growth_planner: () => <GrowthPlannerIcon size={ICON_SIZE} color={ICON_COLOR} strokeWidth={2} />,
  libro_compras: () => <ShoppingCartRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  fitness: () => <WorkoutIcon size={ICON_SIZE} color={ICON_COLOR} strokeWidth={2} />,
  libro_ventas: () => <PointOfSaleRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  libro_gastos: () => <ReceiptLongRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  punto_equilibrio: () => <BalanceRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  inventario: () => <Inventory2RoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
  citas: () => <EventAvailableRoundedIcon style={{ fontSize: ICON_SIZE, color: ICON_COLOR }} />,
};

const CATEGORIAS = ['Todas', 'CoreBase', 'ProfitLens', 'Utilitarios'];

const CAT_CLASS = {
  Todas: '',
  CoreBase: 'cat-corebase',
  ProfitLens: 'cat-profitlens',
  Utilitarios: 'cat-utilitarios',
};

function PremiumPrice({ app }) {
  const price = usePremiumPrice(app);
  return <>
    <span className="app-card__badge">Premium · USD {price.basePrice}/{app.billingPeriod === 'year' ? 'año' : 'mes'}</span>
    {(price.promotional || price.annualPromotional || app.includedSellers) && <div className="app-card__pricing">
      {price.promotional && <div>Normal <s>USD {price.normalPrice}</s> · Promocion hasta el 30/10/2026</div>}
      {price.annualPromotional && <div>Oferta especial: un año por USD {price.annualPrice} · Hasta el 30/10/2026</div>}
      {app.includedSellers && <div>{app.includedSellers} vendedores incluidos · USD {app.additionalSellerPrice}/mes por adicional</div>}
    </div>}
  </>;
}

export default function ToolsExplorer() {
  const navigate = useNavigate();
  const preferences = useToolPreferences(Object.keys(APPS).filter(key => !APPS[key].hidden));
  const coupons = useSurveyCoupons();
  const [busqueda, setBusqueda] = useState('');
  const [catActiva, setCatActiva] = useState('Todas');

  const handleSmartBack = (fallbackRoute) => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(fallbackRoute, { replace: true });
    }
  };

  const tools = useMemo(() => {
    return Object.entries(APPS)
      .filter(([, app]) => !app.hidden)
      .map(([key, app]) => {
        const Icon = APP_ICONS[key];
        return {
          key,
          ...app,
          icon: Icon ? <Icon /> : null,
        };
      })
      .filter((tool) => {
        const matchBusqueda =
          !busqueda || tool.title.toLowerCase().includes(busqueda.toLowerCase());
        const matchCat = catActiva === 'Todas' || tool.categoria === catActiva;
        return matchBusqueda && matchCat;
      }).sort((a,b)=>preferences.keys.indexOf(a.key)-preferences.keys.indexOf(b.key));
  }, [busqueda, catActiva, preferences.keys]);

  return (
    <div className="app-shell apps-page">
      <Helmet>
        <title>Herramientas — Devssoft</title>
        <meta
          name="description"
          content="Accede a las herramientas Devssoft: inventario, POS, CRM, citas y utilitarios para tu negocio."
        />
      </Helmet>

      <AppsNav />

      <main className="apps-main">
        <div className="apps-wrap">
          <div className="apps-back">
            <button
              type="button"
              className="apps-btn apps-btn-ghost apps-btn-sm"
              onClick={() => handleSmartBack('/')}
            >
              <ArrowBackRoundedIcon style={{ fontSize: 16 }} />
              Volver
            </button>
          </div>

          <div className="apps-hero">
            <span className="apps-eyebrow">Suite de herramientas</span>
            <h1>Tus herramientas</h1>
            <p>
              Accede a los sistemas que usas para operar tu negocio. Inventario, POS, CRM,
              citas y utilitarios en un solo lugar.
            </p>
          </div>

          {coupons.banner}
          {preferences.error&&<p role="alert">{preferences.error}</p>}
          <div className="apps-toolbar">
            <div className="apps-search">
              <span className="apps-search-icon" aria-hidden="true">
                <SearchRoundedIcon style={{ fontSize: 18 }} />
              </span>
              <input
                type="search"
                placeholder="Buscar herramienta..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                aria-label="Buscar herramienta"
              />
            </div>
            <div className="apps-filters" role="group" aria-label="Filtrar por categoría">
              {CATEGORIAS.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`apps-filter${catActiva === cat ? ` is-active ${CAT_CLASS[cat]}` : ''}`}
                  onClick={() => setCatActiva(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {tools.length === 0 ? (
            <div className="apps-empty">
              {catActiva === 'ProfitLens' && (
                <img src={profitlensPng} alt="ProfitLens" />
              )}
              <h2>Próximamente</h2>
              <p>Estamos trabajando en nuevas herramientas para esta categoría.</p>
            </div>
          ) : (
            <>
              <p className="apps-count">Ancla tus favoritas y usa las flechas para elegir su orden. Se guarda en tu cuenta. <br/>
                {tools.length} herramienta{tools.length !== 1 ? 's' : ''}
              </p>
              <div className="apps-grid">
                {tools.map((tool) => (
                  <article key={tool.key} className="app-card">
                    <div className="app-card__preferences"><button type="button" aria-label={(preferences.prefs.pinned.includes(tool.key)?'Desanclar ':'Anclar ')+tool.title} aria-pressed={preferences.prefs.pinned.includes(tool.key)} disabled={preferences.busy} onClick={()=>preferences.toggle(tool.key)}><PushPinOutlinedIcon fontSize="small"/></button>{[-1,1].map(delta=>{const group=preferences.prefs.pinned.includes(tool.key)?preferences.prefs.pinned:preferences.keys.filter(k=>!preferences.prefs.pinned.includes(k));const index=group.indexOf(tool.key);return <button type="button" key={delta} aria-label={(delta<0?'Subir ':'Bajar ')+tool.title} disabled={preferences.busy||index+delta<0||index+delta>=group.length} onClick={()=>preferences.move(tool.key,delta)}>{delta<0?<ArrowUpwardIcon fontSize="small"/>:<ArrowDownwardIcon fontSize="small"/>}</button>;})}</div>
                    <div className="app-card__icon">{tool.icon}</div>
                    <span className="app-card__cat">{tool.categoria}</span>
                    <h3>{tool.title}</h3>
                    <p>{tool.description}</p>
                    {tool.premium ? <PremiumPrice app={tool} /> : <span className="app-card__badge">Gratis{tool.key === 'corebase_crm_v1' ? ' hasta 3 vendedores · Extra: USD 1/mes o USD 10/año' : ''}</span>}
                    {coupons.canRedeem(tool.key)&&<button type="button" className="apps-btn apps-btn-sm" onClick={()=>coupons.choose(tool.key)}>Canjear cupón · 3 meses gratis</button>}
                    <button
                      type="button"
                      className="apps-btn apps-btn-ghost apps-btn-sm app-card__action"
                      onClick={() => navigate(tool.link)}
                    >
                      Abrir herramienta
                      <ChevronRightRoundedIcon style={{ fontSize: 16 }} />
                    </button>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
